import { useMemo, useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../shared/ui-components/Button/Button';
import iconTrashcan from '../../assets/icon-trashcan.svg';
import iconInfoFutterverbrauch from '../../assets/icon-info-futterverbrauch.svg';
import iconPencil from '../../assets/icon-pencil.svg';
import iconKundennummer from '../../assets/icon-kundennummer.svg';
import iconSiloCapacity from '../../assets/icon-silo-capacity.svg';
import iconSiloVolume from '../../assets/icon-silo-volume.svg';
import iconEntnahmemenge from '../../assets/icon-entnahmemenge.svg';
import iconWarnschwelle from '../../assets/icon-warnschwelle.svg';
import { showConfirmationDialog } from '../../shared/ui-components/ConfirmationDialog/confirmationDialog';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import styles from './SiloAnlegen.module.scss';
import { fetchSiloDetails, updateSilo, createSilo, deleteSilo } from '../../store/Silo.store';
import { getWarenempfaenger, WarenempfaengerSelectors } from '../../store/Warenempfaenger.store';
import Select from 'react-select';
import { createCustomStyles } from '../../shared/ui-components/SelectInput/CustomStyles';
import { MenuList } from '../../shared/content-components/Artikelauswahl/MenuList/MenuList';
import SiloIcon from '../SiloUebersicht/SiloIcon';
import type { RootState } from '../../configureStore';
import LoadingSpinner from '../../shared/ui-components/LoadingSpinner/LoadingSpinner';
// import NonFormikCheckbox from '../../shared/ui-components/CheckBox/NonFormikCheckbox';

const parseDecimal = (s: string): number => Number(String(s).trim().replace(',', '.'));

const formatDecimalForDisplay = (value: number, decimals = 1): string => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return n.toFixed(decimals).replace('.', ',');
};

const SiloAnlegen = (): JSX.Element => {
    type CustomerOption = { value: string; label: string };
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const editSiloId = params.get('siloId');
    const currentSiloDetails = useSelector((s: RootState) => s.silo.currentSiloDetails);
    const createError = useSelector((s: RootState) => s.silo.createError);
    const isCreating = useSelector((s: RootState) => s.silo.isCreating);
    const updateError = useSelector((s: RootState) => s.silo.updateError);
    const isUpdating = useSelector((s: RootState) => s.silo.isUpdating);
    const isDeleting = useSelector((s: RootState) => s.silo.isDeleting);
    const deleteError = useSelector((s: RootState) => s.silo.deleteError);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [deleteRequested, setDeleteRequested] = useState<boolean>(false);

    const [editSiloNumberOrName, setEditSiloNumberOrName] = useState<string>('');
    const [editCustomerNumber, setEditCustomerNumber] = useState<string>('');
    const [editCapacityTons, setEditCapacityTons] = useState<string>('');
    const [editInitialFillTons, setEditInitialFillTons] = useState<string>('');
    const [editVolumeM3, setEditVolumeM3] = useState<string>('');
    const [editCalcConsumptionEnabled, setEditCalcConsumptionEnabled] = useState<boolean>(false);
    const [editWarningsEnabled, setEditWarningsEnabled] = useState<boolean>(false);
    const [editDailyWithdrawalKg, setEditDailyWithdrawalKg] = useState<string>('');
    const [editThresholdPercent, setEditThresholdPercent] = useState<string>('');
    const warenempfaengerList = useSelector(WarenempfaengerSelectors.all);

    const isSensorSilo = !!(editSiloId && currentSiloDetails && currentSiloDetails.hasSensorData);

    useEffect(() => {
        if (!warenempfaengerList || warenempfaengerList.length === 0) {
            dispatch(getWarenempfaenger());
        }
    }, [dispatch, warenempfaengerList]);

    useMemo(() => {
        if (editSiloId) {
            dispatch(fetchSiloDetails(editSiloId));
        }
    }, [dispatch, editSiloId]);

    // Keine zusätzliche Suche nötig; wir verwenden die Debitoren aus dem User-State

    useEffect(() => {
        if (!editSiloId) {
            setEditSiloNumberOrName('');
            setEditCustomerNumber('');
            setEditCapacityTons('');
            setEditInitialFillTons('');
            setEditVolumeM3('');
            setEditCalcConsumptionEnabled(false);
            setEditWarningsEnabled(false);
            setEditDailyWithdrawalKg('');
            setEditThresholdPercent('');
            setErrors({});
        }
    }, [editSiloId]);

    // Handle backend errors
    useEffect(() => {
        if (createError) {
            console.log('Backend error received:', createError); // Debug log
            const newErrors: { [key: string]: string } = {};

            if (createError.includes('PARTNER_ERROR: Kundennummer unbekannt')) {
                newErrors.customerNumber = 'Diese Kundennummer existiert nicht. Bitte überprüfen Sie die Eingabe.';
            } else if (createError.includes('DUPLICATE_ERROR: Silo-Name/-Nummer bereits vergeben')) {
                newErrors.siloName = 'Dieser Siloname ist bereits vergeben. Bitte wählen Sie einen anderen Namen.';
            } else if (createError.includes('VALIDATION_ERROR: Kapazität muss > 0 sein')) {
                newErrors.capacityTons = 'Die Kapazität muss größer als 0 sein.';
            } else if (createError.includes('VALIDATION_ERROR: Initialer Füllstand darf Kapazität nicht überschreiten')) {
                newErrors.initialFillTons = 'Der initiale Füllstand darf die Kapazität nicht überschreiten.';
            } else if (createError.includes('VALIDATION_ERROR: Name/Nummer ist erforderlich')) {
                newErrors.siloName = 'Bitte geben Sie einen Silonamen ein.';
            } else if (createError.includes('Ein unbekannter Fehler ist aufgetreten')) {
                newErrors.general = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
            } else {
                if (createError.includes('Kundennummer unbekannt')) {
                    newErrors.customerNumber = 'Diese Kundennummer existiert nicht. Bitte überprüfen Sie die Eingabe.';
                } else if (createError.includes('Silo-Name/-Nummer bereits vergeben') || createError.includes('bereits vergeben')) {
                    newErrors.siloName = 'Dieser Siloname ist bereits vergeben. Bitte wählen Sie einen anderen Namen.';
                } else if (createError.includes('Kapazität muss > 0 sein')) {
                    newErrors.capacityTons = 'Die Kapazität muss größer als 0 sein.';
                } else if (createError.includes('Initialer Füllstand darf Kapazität nicht überschreiten')) {
                    newErrors.initialFillTons = 'Der initiale Füllstand darf die Kapazität nicht überschreiten.';
                } else if (createError.includes('Name/Nummer ist erforderlich')) {
                    newErrors.siloName = 'Bitte geben Sie einen Silonamen ein.';
                } else {
                    newErrors.general = createError || 'Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
                }
            }

            setErrors(newErrors);
            setIsSaving(false);
        }
    }, [createError]);

    // Handle update errors (edit mode)
    useEffect(() => {
        if (updateError) {
            console.log('Backend update error received:', updateError);
            const newErrors: { [key: string]: string } = {};

            if (updateError.includes('PARTNER_ERROR: Kundennummer unbekannt')) {
                newErrors.customerNumber = 'Diese Kundennummer existiert nicht. Bitte überprüfen Sie die Eingabe.';
            } else if (
                updateError.includes('DUPLICATE_ERROR: Silo-Name/-Nummer bereits vergeben') ||
                updateError.includes('Silo-Name/-Nummer bereits vergeben') ||
                updateError.toLowerCase().includes('duplicate')
            ) {
                newErrors.siloName = 'Dieser Siloname ist bereits vergeben. Bitte wählen Sie einen anderen Namen.';
            } else if (updateError.includes('Kapazität muss > 0 sein')) {
                newErrors.capacityTons = 'Die Kapazität muss größer als 0 sein.';
            } else if (updateError.includes('Initialer Füllstand darf Kapazität nicht überschreiten')) {
                newErrors.initialFillTons = 'Der initiale Füllstand darf die Kapazität nicht überschreiten.';
            } else if (updateError.includes('Name/Nummer ist erforderlich')) {
                newErrors.siloName = 'Bitte geben Sie einen Silonamen ein.';
            } else {
                newErrors.general = 'Speichern fehlgeschlagen. Bitte versuchen Sie es erneut.';
            }

            setErrors(newErrors);
            setIsSaving(false);
        }
    }, [updateError]);

    useEffect(() => {
        if (!editSiloId && !isCreating && !createError && isSaving) {
            history.push('/meine-silos');
        }
    }, [isCreating, createError, isSaving, editSiloId, history]);

    // Navigate after successful update (edit mode)
    useEffect(() => {
        if (editSiloId && !isUpdating && !updateError && isSaving) {
            history.push('/meine-silos');
        }
    }, [editSiloId, isUpdating, updateError, isSaving, history]);

    // Navigate back to overview after successful delete
    useEffect(() => {
        if (deleteRequested && !isDeleting && !deleteError) {
            history.push('/meine-silos');
        }
    }, [deleteRequested, isDeleting, deleteError, history]);

    useMemo(() => {
        if (editSiloId && currentSiloDetails) {
            setEditSiloNumberOrName(currentSiloDetails.siloName || '');
            setEditCustomerNumber(currentSiloDetails.customerNumber || '');
            setEditCapacityTons(currentSiloDetails.capacityTons != null ? formatDecimalForDisplay(currentSiloDetails.capacityTons, 1) : '');
            if (
                currentSiloDetails.currentPercent != null &&
                currentSiloDetails.capacityTons != null &&
                currentSiloDetails.capacityTons > 0
            ) {
                const currentTons = (currentSiloDetails.currentPercent / 100) * currentSiloDetails.capacityTons;
                setEditInitialFillTons(formatDecimalForDisplay(currentTons, 1));
            } else {
                setEditInitialFillTons('');
            }
            setEditVolumeM3(currentSiloDetails.volumeM3 != null ? formatDecimalForDisplay(currentSiloDetails.volumeM3, 1) : '');
            const consumptionEnabled = Boolean(currentSiloDetails.consumptionEnabled);
            setEditCalcConsumptionEnabled(consumptionEnabled);
            setEditDailyWithdrawalKg(
                consumptionEnabled && currentSiloDetails.dailyConsumptionKg != null ? String(currentSiloDetails.dailyConsumptionKg) : ''
            );

            const warningsEnabled = Boolean(currentSiloDetails.warningsEnabled);
            setEditWarningsEnabled(warningsEnabled);
            setEditThresholdPercent(
                warningsEnabled && currentSiloDetails.thresholdPercent != null ? String(currentSiloDetails.thresholdPercent) : ''
            );
        }
    }, [editSiloId, currentSiloDetails]);

    const validateField = (fieldName: string, newValue: string): void => {
        const newErrors = { ...errors };

        // Get current values, using newValue for the field being changed
        const getRawValue = (field: string): string | undefined => {
            if (field === fieldName) return newValue;
            if (field === 'capacityTons') return editCapacityTons;
            if (field === 'initialFillTons') return editInitialFillTons;
            if (field === 'volumeM3') return editVolumeM3;
            if (field === 'dailyWithdrawalKg') return editDailyWithdrawalKg;
            if (field === 'thresholdPercent') return editThresholdPercent;
            return undefined;
        };

        const getValue = (field: string): number | undefined => {
            const val = getRawValue(field);
            if (val === undefined) return undefined;
            return val.trim() !== '' ? parseDecimal(val) : undefined;
        };

        const cap = getValue('capacityTons');
        const init = getValue('initialFillTons');
        const vol = getValue('volumeM3');
        const dailyWithdrawalRaw = getRawValue('dailyWithdrawalKg')?.trim() ?? '';
        const dailyWithdrawal = getValue('dailyWithdrawalKg');
        const thresholdRaw = getRawValue('thresholdPercent')?.trim() ?? '';
        const threshold = getValue('thresholdPercent');

        // Clear errors for fields that might be affected
        if (fieldName === 'capacityTons' || fieldName === 'initialFillTons') {
            delete newErrors.capacityTons;
            delete newErrors.initialFillTons;
        } else {
            delete newErrors[fieldName];
        }

        // Validate capacity
        if (cap !== undefined && !(cap > 0)) {
            newErrors.capacityTons = 'Die Kapazität muss größer als 0 sein.';
        }

        // Validate initial fill
        if (init !== undefined && init < 0) {
            newErrors.initialFillTons = 'Der aktuelle Füllstand muss größer oder gleich 0 sein.';
        }
        if (cap !== undefined && init !== undefined && init > cap) {
            newErrors.initialFillTons = 'Der initiale Füllstand darf die Kapazität nicht überschreiten.';
        }

        // Validate volume
        if (vol !== undefined && !(vol > 0)) {
            newErrors.volumeM3 = 'Das Volumen muss größer als 0 sein.';
        }

        if (editCalcConsumptionEnabled) {
            if (dailyWithdrawalRaw !== '' && (dailyWithdrawal === undefined || !(dailyWithdrawal > 0))) {
                newErrors.dailyWithdrawalKg = 'Die tägliche Entnahmemenge muss größer als 0 sein.';
            }
        }

        if (editWarningsEnabled) {
            if (thresholdRaw !== '' && (threshold === undefined || threshold < 0 || threshold > 100)) {
                newErrors.thresholdPercent = 'Die Warnschwelle muss zwischen 0 und 100 liegen.';
            }
        }

        setErrors(newErrors);
    };

    const onSave = async (): Promise<void> => {
        const newErrors: { [key: string]: string } = {};

        if (!editSiloNumberOrName.trim()) {
            newErrors.siloName = 'Bitte geben Sie einen Silonamen ein.';
        }

        if (!editCustomerNumber.trim()) {
            newErrors.customerNumber = 'Bitte wählen Sie eine Kundennummer aus.';
        }

        if (editSiloId && isSensorSilo) {
            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }
            setErrors({});
            setIsSaving(true);
            try {
                await dispatch(
                    updateSilo({
                        siloId: editSiloId,
                        siloName: editSiloNumberOrName,
                        customerNumber: editCustomerNumber,
                    })
                );
            } catch (error) {
                console.error('Error saving silo:', error);
                setIsSaving(false);
            }
            return;
        }

        const cap = editCapacityTons.trim() !== '' ? parseDecimal(editCapacityTons) : undefined;
        const init = editInitialFillTons.trim() !== '' ? parseDecimal(editInitialFillTons) : undefined;
        const vol = editVolumeM3.trim() !== '' ? parseDecimal(editVolumeM3) : undefined;
        const thresholdRaw = editThresholdPercent.trim();
        const threshold = editThresholdPercent.trim() !== '' ? parseDecimal(editThresholdPercent) : undefined;

        if (cap !== undefined && !(cap > 0)) {
            newErrors.capacityTons = 'Die Kapazität muss größer als 0 sein.';
        }
        if (vol !== undefined && !(vol > 0)) {
            newErrors.volumeM3 = 'Das Volumen muss größer als 0 sein.';
        }
        if (init !== undefined && init < 0) {
            newErrors.initialFillTons = 'Der aktuelle Füllstand muss größer oder gleich 0 sein.';
        }
        if (cap !== undefined && init !== undefined && init > cap) {
            newErrors.initialFillTons = 'Der initiale Füllstand darf die Kapazität nicht überschreiten.';
        }
        if (editWarningsEnabled) {
            if (thresholdRaw === '') {
                newErrors.thresholdPercent = 'Bitte geben Sie eine Warnschwelle zwischen 0 und 100 an.';
            } else if (threshold === undefined || threshold < 0 || threshold > 100) {
                newErrors.thresholdPercent = 'Die Warnschwelle muss zwischen 0 und 100 liegen.';
            }
        }

        const dailyWithdrawalRaw = editDailyWithdrawalKg.trim();
        const dailyWithdrawal = editDailyWithdrawalKg.trim() !== '' ? parseDecimal(editDailyWithdrawalKg) : undefined;
        if (editCalcConsumptionEnabled) {
            if (dailyWithdrawalRaw === '') {
                newErrors.dailyWithdrawalKg = 'Bitte geben Sie eine tägliche Entnahmemenge größer als 0 an.';
            } else if (dailyWithdrawal === undefined || !(dailyWithdrawal > 0)) {
                newErrors.dailyWithdrawalKg = 'Die tägliche Entnahmemenge muss größer als 0 sein.';
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setIsSaving(true);

        try {
            if (editSiloId) {
                const payload = {
                    siloId: editSiloId,
                    siloName: editSiloNumberOrName,
                    customerNumber: editCustomerNumber,
                    capacityTons: editCapacityTons === '' ? undefined : parseDecimal(editCapacityTons),
                    initialFillTons: editInitialFillTons === '' ? undefined : parseDecimal(editInitialFillTons),
                    volumeM3: editVolumeM3 === '' ? undefined : parseDecimal(editVolumeM3),
                    consumptionEnabled: editCalcConsumptionEnabled,
                    dailyConsumptionKg:
                        editCalcConsumptionEnabled && editDailyWithdrawalKg !== '' ? parseDecimal(editDailyWithdrawalKg) : undefined,
                    warningsEnabled: editWarningsEnabled,
                    thresholdPercent: editWarningsEnabled && editThresholdPercent !== '' ? parseDecimal(editThresholdPercent) : undefined,
                };
                await dispatch(updateSilo(payload));
            } else {
                const createPayload = {
                    siloNumberOrName: editSiloNumberOrName,
                    customerNumber: editCustomerNumber,
                    capacityTons: editCapacityTons === '' ? undefined : parseDecimal(editCapacityTons),
                    initialFillLevel: editInitialFillTons === '' ? undefined : parseDecimal(editInitialFillTons),
                    volumeM3: editVolumeM3 === '' ? undefined : parseDecimal(editVolumeM3),
                    consumptionEnabled: editCalcConsumptionEnabled,
                    dailyConsumptionKg:
                        editCalcConsumptionEnabled && editDailyWithdrawalKg !== '' ? parseDecimal(editDailyWithdrawalKg) : undefined,
                    warningsEnabled: editWarningsEnabled,
                    thresholdPercent: editWarningsEnabled && editThresholdPercent !== '' ? parseDecimal(editThresholdPercent) : undefined,
                };
                await dispatch(createSilo(createPayload));
            }
        } catch (error) {
            console.error('Error saving silo:', error);
            setIsSaving(false);
        }
    };

    const computedPercent: number = useMemo(() => {
        if (editSiloId && currentSiloDetails && currentSiloDetails.hasSensorData && currentSiloDetails.currentPercent != null) {
            return Math.max(0, Math.min(100, Math.round(currentSiloDetails.currentPercent)));
        }
        const cap = editCapacityTons.trim() !== '' ? parseDecimal(editCapacityTons) : NaN;
        const init = editInitialFillTons.trim() !== '' ? parseDecimal(editInitialFillTons) : NaN;
        if (editSiloId && currentSiloDetails && !currentSiloDetails.hasSensorData) {
            if (!Number.isFinite(cap) || cap <= 0 || !Number.isFinite(init)) {
                return -1;
            }
        }
        if (!Number.isFinite(cap) || cap <= 0 || !Number.isFinite(init)) {
            return 0;
        }
        const pct = (init / cap) * 100;
        return Math.max(0, Math.min(100, Math.round(pct)));
    }, [editCapacityTons, editInitialFillTons, editSiloId, currentSiloDetails]);

    return (
        <LoadingSpinner isLoading={deleteRequested}>
            <div className={styles.container}>
                <div className={styles.headerRow}>
                    <h1 className={styles.title}>{editSiloId ? 'Silo bearbeiten' : 'Neues Silo anlegen'}</h1>
                    {editSiloId && currentSiloDetails && currentSiloDetails.hasSensorData === false && (
                        <div className={styles.menuWrapper}>
                            <button
                                type="button"
                                aria-label="Mehr Optionen"
                                onClick={() => setMenuOpen((v) => !v)}
                                className={styles.menuTrigger}
                            >
                                ⋮
                            </button>
                            {menuOpen && (
                                <>
                                    <div className={styles.menuOverlay} onClick={() => setMenuOpen(false)} />
                                    <div className={styles.menuPanel}>
                                        <button
                                            type="button"
                                            className={styles.menuItemDelete}
                                            onClick={() => {
                                                setMenuOpen(false);
                                                showConfirmationDialog({
                                                    message: <>Sind Sie sich sicher, dass Sie dieses Silo löschen möchten?</>,
                                                    okButtonText: 'Silo löschen',
                                                    onOkCallback: () => {
                                                        setDeleteRequested(true);
                                                        dispatch(deleteSilo(editSiloId));
                                                    },
                                                });
                                            }}
                                        >
                                            <img src={iconTrashcan} alt="Löschen" className={styles.menuItemIcon} />
                                            Silo löschen
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.generalSection}>
                    <div className={styles.fieldsColumn}>
                        <h3 className={`${styles.sectionTitle} ${styles.sectionTitleAllgemeines}`} style={{ marginLeft: 2 }}>
                            Allgemeines
                        </h3>
                        <div className={styles.frame130}>
                            <div className={styles.frame152}>
                                <div className={styles.frame152Left}>
                                    <div className={styles.formRow}>
                                        <label>Siloname*</label>
                                        <div className={`${styles.inputNameFrame} ${errors.siloName ? styles.inputError : ''}`}>
                                            <img src={iconPencil} alt="" className={styles.inputNameIcon} aria-hidden />
                                            <input
                                                className={styles.input}
                                                value={editSiloNumberOrName}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setEditSiloNumberOrName(value);
                                                    if (errors.siloName && value.trim() !== '') {
                                                        setErrors((prev) => {
                                                            const next = { ...prev };
                                                            delete next.siloName;
                                                            return next;
                                                        });
                                                    }
                                                }}
                                                placeholder=""
                                            />
                                        </div>
                                        {errors.siloName && <span className={styles.errorText}>{errors.siloName}</span>}
                                    </div>
                                    <div className={`${styles.formRow} ${styles.formRowKundennummer}`}>
                                        <label>Kundennummer*</label>
                                        <div className={`${styles.inputNameFrame} ${errors.customerNumber ? styles.inputError : ''}`}>
                                            <img src={iconKundennummer} alt="" className={styles.inputNameIcon} aria-hidden />
                                            <div className={styles.inputSelectWrap}>
                                                <Select<CustomerOption, false>
                                                    className={styles.inputSelect}
                                                    classNamePrefix="react_select__form-customer"
                                                    options={(warenempfaengerList || []).map((p) => ({
                                                        value: p.partnerNummer,
                                                        label: `${p.partnerNummer} ${p.hauptname}`,
                                                    }))}
                                                    value={
                                                        editCustomerNumber
                                                            ? (() => {
                                                                  const selected = warenempfaengerList?.find(
                                                                      (p) => p.partnerNummer === editCustomerNumber
                                                                  );
                                                                  return {
                                                                      value: editCustomerNumber,
                                                                      label: selected
                                                                          ? `${selected.partnerNummer} ${selected.hauptname}`
                                                                          : editCustomerNumber,
                                                                  } as CustomerOption;
                                                              })()
                                                            : null
                                                    }
                                                    onChange={(option: CustomerOption | null): void => {
                                                        const value = option ? option.value : '';
                                                        setEditCustomerNumber(value);
                                                        if (errors.customerNumber && value !== '') {
                                                            setErrors((prev) => {
                                                                const next = { ...prev };
                                                                delete next.customerNumber;
                                                                return next;
                                                            });
                                                        }
                                                    }}
                                                    menuPlacement="auto"
                                                    placeholder="Kundennummer auswählen"
                                                    noOptionsMessage={() => 'Keine Einträge'}
                                                    isClearable={false}
                                                    isDisabled={isSensorSilo}
                                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                    components={{ MenuList: MenuList as any }}
                                                    styles={createCustomStyles(false, false, 'left', false)}
                                                />
                                            </div>
                                        </div>
                                        {errors.customerNumber && <span className={styles.errorText}>{errors.customerNumber}</span>}
                                    </div>
                                </div>
                                <div className={styles.siloGraphic}>
                                    <SiloIcon percent={computedPercent} width={138} height={138} />
                                </div>
                            </div>
                            <div className={styles.formRowGrid}>
                                <div className={styles.formRow}>
                                    <label>Kapazität (t)</label>
                                    <div className={`${styles.inputFieldFrame} ${errors.capacityTons ? styles.inputError : ''}`}>
                                        <img src={iconSiloCapacity} alt="" className={styles.inputIconLeft} aria-hidden />
                                        <input
                                            className={styles.inputInField}
                                            value={editCapacityTons}
                                            onChange={(e) => {
                                                const v = e.target.value.replace('.', ',');
                                                setEditCapacityTons(v);
                                                validateField('capacityTons', v);
                                            }}
                                            inputMode="decimal"
                                            disabled={isSensorSilo}
                                        />
                                        <span className={styles.iconGroup}>
                                            <button
                                                type="button"
                                                className={styles.iconBtn}
                                                onClick={() => {
                                                    const newValue = formatDecimalForDisplay(
                                                        parseDecimal(editCapacityTons || '0') - 0.1,
                                                        1
                                                    );
                                                    setEditCapacityTons(newValue);
                                                    validateField('capacityTons', newValue);
                                                }}
                                                aria-label="minus"
                                                disabled={isSensorSilo}
                                            >
                                                &minus;
                                            </button>
                                            <span className={styles.sep}></span>
                                            <button
                                                type="button"
                                                className={styles.iconBtn}
                                                onClick={() => {
                                                    const newValue = formatDecimalForDisplay(
                                                        parseDecimal(editCapacityTons || '0') + 0.1,
                                                        1
                                                    );
                                                    setEditCapacityTons(newValue);
                                                    validateField('capacityTons', newValue);
                                                }}
                                                aria-label="plus"
                                                disabled={isSensorSilo}
                                            >
                                                +
                                            </button>
                                        </span>
                                    </div>
                                    {errors.capacityTons && <span className={styles.errorText}>{errors.capacityTons}</span>}
                                </div>
                                <div className={styles.formRow}>
                                    <label>aktueller Füllstand (t)</label>
                                    <div className={`${styles.inputFieldFrame} ${errors.initialFillTons ? styles.inputError : ''}`}>
                                        <img src={iconSiloCapacity} alt="" className={styles.inputIconLeft} aria-hidden />
                                        <input
                                            className={styles.inputInField}
                                            value={editInitialFillTons}
                                            onChange={(e) => {
                                                const v = e.target.value.replace('.', ',');
                                                setEditInitialFillTons(v);
                                                validateField('initialFillTons', v);
                                            }}
                                            inputMode="decimal"
                                            disabled={isSensorSilo}
                                        />
                                        <span className={styles.iconGroup}>
                                            <button
                                                type="button"
                                                className={styles.iconBtn}
                                                onClick={() => {
                                                    const newValue = formatDecimalForDisplay(
                                                        parseDecimal(editInitialFillTons || '0') - 0.1,
                                                        1
                                                    );
                                                    setEditInitialFillTons(newValue);
                                                    validateField('initialFillTons', newValue);
                                                }}
                                                aria-label="minus"
                                                disabled={isSensorSilo}
                                            >
                                                &minus;
                                            </button>
                                            <span className={styles.sep}></span>
                                            <button
                                                type="button"
                                                className={styles.iconBtn}
                                                onClick={() => {
                                                    const newValue = formatDecimalForDisplay(
                                                        parseDecimal(editInitialFillTons || '0') + 0.1,
                                                        1
                                                    );
                                                    setEditInitialFillTons(newValue);
                                                    validateField('initialFillTons', newValue);
                                                }}
                                                aria-label="plus"
                                                disabled={isSensorSilo}
                                            >
                                                +
                                            </button>
                                        </span>
                                    </div>
                                    {errors.initialFillTons && <span className={styles.errorText}>{errors.initialFillTons}</span>}
                                </div>
                            </div>

                            <div className={`${styles.formRow} ${styles.doubleWidthRow}`}>
                                <label>
                                    Volumen (m<sup>3</sup>)
                                </label>
                                <div className={`${styles.inputFieldFrame} ${errors.volumeM3 ? styles.inputError : ''}`}>
                                    <img src={iconSiloVolume} alt="" className={styles.inputIconLeft} aria-hidden />
                                    <input
                                        className={styles.inputInField}
                                        value={editVolumeM3}
                                        onChange={(e) => {
                                            const v = e.target.value.replace('.', ',');
                                            setEditVolumeM3(v);
                                            validateField('volumeM3', v);
                                        }}
                                        inputMode="decimal"
                                        disabled={isSensorSilo}
                                    />
                                    <span className={styles.iconGroup}>
                                        <button
                                            type="button"
                                            className={styles.iconBtn}
                                            onClick={() => {
                                                const newValue = formatDecimalForDisplay(parseDecimal(editVolumeM3 || '0') - 0.1, 1);
                                                setEditVolumeM3(newValue);
                                                validateField('volumeM3', newValue);
                                            }}
                                            aria-label="minus"
                                            disabled={isSensorSilo}
                                        >
                                            &minus;
                                        </button>
                                        <span className={styles.sep}></span>
                                        <button
                                            type="button"
                                            className={styles.iconBtn}
                                            onClick={() => {
                                                const newValue = formatDecimalForDisplay(parseDecimal(editVolumeM3 || '0') + 0.1, 1);
                                                setEditVolumeM3(newValue);
                                                validateField('volumeM3', newValue);
                                            }}
                                            aria-label="plus"
                                            disabled={isSensorSilo}
                                        >
                                            +
                                        </button>
                                    </span>
                                </div>
                                {errors.volumeM3 && <span className={styles.errorText}>{errors.volumeM3}</span>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.frame131}>
                    <h3 className={`${styles.sectionTitle} ${styles.sectionTitleFutterverbrauch}`} style={{ marginLeft: 2 }}>
                        Futterverbrauch
                    </h3>
                    <div className={styles.frame149}>
                        <div className={styles.frame147}>
                            <div className={styles.inlineRow}>
                                <label className={styles.yellowSwitch} htmlFor="edit-calc-consumption-toggle">
                                    <input
                                        id="edit-calc-consumption-toggle"
                                        type="checkbox"
                                        checked={editCalcConsumptionEnabled}
                                        onChange={(e) => {
                                            const enabled = e.target.checked;
                                            setEditCalcConsumptionEnabled(enabled);
                                            const currentValue = editDailyWithdrawalKg.trim();
                                            if (!enabled) {
                                                setEditDailyWithdrawalKg('');
                                                setErrors((prev) => {
                                                    const next = { ...prev };
                                                    delete next.dailyWithdrawalKg;
                                                    return next;
                                                });
                                                return;
                                            }
                                            if (currentValue === '') {
                                                setErrors((prev) => {
                                                    const next = { ...prev };
                                                    delete next.dailyWithdrawalKg;
                                                    return next;
                                                });
                                            } else {
                                                validateField('dailyWithdrawalKg', editDailyWithdrawalKg);
                                            }
                                        }}
                                        disabled={isSensorSilo}
                                    />
                                    <svg
                                        className={styles.toggleSvg}
                                        width="40"
                                        height="20"
                                        viewBox="0 0 40 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden
                                    >
                                        <rect
                                            x="0.5"
                                            y="0.5"
                                            width="39"
                                            height="19"
                                            rx="9.5"
                                            fill={editCalcConsumptionEnabled ? '#E0B300' : '#ccc'}
                                            stroke={editCalcConsumptionEnabled ? '#E0B300' : '#ccc'}
                                        />
                                        <circle
                                            className={styles.toggleKnob}
                                            cx="10"
                                            cy="10"
                                            r="9"
                                            fill="white"
                                            style={{
                                                transform: editCalcConsumptionEnabled ? 'translateX(20px)' : 'translateX(0)',
                                            }}
                                        />
                                    </svg>
                                </label>
                                <span className={styles.toggleLabelGroup}>
                                    <span className={styles.toggleLabelText}>Futterverbrauch berechnen</span>
                                    <Tippy
                                        content="Wenn Ihr Silo nicht mit Sensorik ausgestattet ist, können Sie hier eine tägliche Entnahmemenge festlegen. Der geschätzte Füllstand wird dann jeden Tag automatisch um diesen Wert reduziert."
                                        placement="top"
                                        maxWidth={360}
                                        delay={200}
                                        arrow={false}
                                        className={styles.infoTooltip}
                                    >
                                        <span className={styles.infoIconWrap} aria-label="Verbrauchsberechnung einstellen">
                                            <img src={iconInfoFutterverbrauch} alt="" className={styles.infoIcon} />
                                        </span>
                                    </Tippy>
                                </span>
                            </div>
                            <div className={`${styles.formRow} ${styles.doubleWidthRow}`}>
                                <label>Tägliche Entnahmemenge (kg)</label>
                                <div className={`${styles.inputFieldFrame} ${errors.dailyWithdrawalKg ? styles.inputError : ''}`}>
                                    <img src={iconEntnahmemenge} alt="" className={styles.inputIconLeft} aria-hidden />
                                    <input
                                        className={styles.inputInField}
                                        value={editDailyWithdrawalKg}
                                        onChange={(e) => {
                                            const v = e.target.value.replace('.', ',');
                                            setEditDailyWithdrawalKg(v);
                                            validateField('dailyWithdrawalKg', v);
                                        }}
                                        inputMode="numeric"
                                        disabled={isSensorSilo}
                                    />
                                    <span className={styles.iconGroup}>
                                        <button
                                            type="button"
                                            className={styles.iconBtn}
                                            onClick={() => {
                                                const newValue = String(parseDecimal(editDailyWithdrawalKg || '0') - 1);
                                                setEditDailyWithdrawalKg(newValue);
                                                validateField('dailyWithdrawalKg', newValue);
                                            }}
                                            aria-label="minus"
                                            disabled={isSensorSilo}
                                        >
                                            &minus;
                                        </button>
                                        <span className={styles.sep}></span>
                                        <button
                                            type="button"
                                            className={styles.iconBtn}
                                            onClick={() => {
                                                const newValue = String(parseDecimal(editDailyWithdrawalKg || '0') + 1);
                                                setEditDailyWithdrawalKg(newValue);
                                                validateField('dailyWithdrawalKg', newValue);
                                            }}
                                            aria-label="plus"
                                            disabled={isSensorSilo}
                                        >
                                            +
                                        </button>
                                    </span>
                                </div>
                                {errors.dailyWithdrawalKg && <span className={styles.errorText}>{errors.dailyWithdrawalKg}</span>}
                            </div>
                        </div>
                        <div className={styles.frame148}>
                            <div className={styles.inlineRow}>
                                <label className={styles.yellowSwitch} htmlFor="edit-silo-warn-toggle">
                                    <input
                                        id="edit-silo-warn-toggle"
                                        type="checkbox"
                                        checked={editWarningsEnabled}
                                        onChange={(e) => {
                                            const enabled = e.target.checked;
                                            setEditWarningsEnabled(enabled);
                                            const currentValue = editThresholdPercent.trim();
                                            if (!enabled) {
                                                setEditThresholdPercent('');
                                                setErrors((prev) => {
                                                    const next = { ...prev };
                                                    delete next.thresholdPercent;
                                                    return next;
                                                });
                                                return;
                                            }
                                            if (currentValue === '') {
                                                setErrors((prev) => {
                                                    const next = { ...prev };
                                                    delete next.thresholdPercent;
                                                    return next;
                                                });
                                            } else {
                                                validateField('thresholdPercent', editThresholdPercent);
                                            }
                                        }}
                                        disabled={isSensorSilo}
                                    />
                                    <svg
                                        className={styles.toggleSvg}
                                        width="40"
                                        height="20"
                                        viewBox="0 0 40 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden
                                    >
                                        <rect
                                            x="0.5"
                                            y="0.5"
                                            width="39"
                                            height="19"
                                            rx="9.5"
                                            fill={editWarningsEnabled ? '#E0B300' : '#ccc'}
                                            stroke={editWarningsEnabled ? '#E0B300' : '#ccc'}
                                        />
                                        <circle
                                            className={styles.toggleKnob}
                                            cx="10"
                                            cy="10"
                                            r="9"
                                            fill="white"
                                            style={{
                                                transform: editWarningsEnabled ? 'translateX(20px)' : 'translateX(0)',
                                            }}
                                        />
                                    </svg>
                                </label>
                                <span className={styles.toggleLabelGroup}>
                                    <span className={styles.toggleLabelText}>Verbrauchswarnungen einschalten</span>
                                    <Tippy
                                        content="Aktivieren Sie die Verbrauchswarnung und legen Sie einen Schwellenwert (in %) fest. Sobald der geschätzte Füllstand diesen Wert unterschreitet, erhalten Sie automatisch eine Warnung."
                                        placement="top"
                                        maxWidth={360}
                                        delay={200}
                                        arrow={false}
                                        className={styles.infoTooltip}
                                    >
                                        <span className={styles.infoIconWrap} aria-label="Verbrauchswarnung aktivieren">
                                            <img src={iconInfoFutterverbrauch} alt="" className={styles.infoIcon} />
                                        </span>
                                    </Tippy>
                                </span>
                            </div>
                            <div className={`${styles.formRow} ${styles.doubleWidthRow}`}>
                                <label>Warnschwelle (%)</label>
                                <div className={`${styles.inputFieldFrame} ${errors.thresholdPercent ? styles.inputError : ''}`}>
                                    <img src={iconWarnschwelle} alt="" className={styles.inputIconLeft} aria-hidden />
                                    <input
                                        className={styles.inputInField}
                                        value={editThresholdPercent}
                                        onChange={(e) => {
                                            const v = e.target.value.replace('.', ',');
                                            setEditThresholdPercent(v);
                                            validateField('thresholdPercent', v);
                                        }}
                                        inputMode="numeric"
                                        disabled={isSensorSilo}
                                    />
                                    <span className={styles.iconGroup}>
                                        <button
                                            type="button"
                                            className={styles.iconBtn}
                                            onClick={() => {
                                                const newValue = String(parseDecimal(editThresholdPercent || '0') - 1);
                                                setEditThresholdPercent(newValue);
                                                validateField('thresholdPercent', newValue);
                                            }}
                                            aria-label="minus"
                                            disabled={isSensorSilo}
                                        >
                                            &minus;
                                        </button>
                                        <span className={styles.sep}></span>
                                        <button
                                            type="button"
                                            className={styles.iconBtn}
                                            onClick={() => {
                                                const newValue = String(parseDecimal(editThresholdPercent || '0') + 1);
                                                setEditThresholdPercent(newValue);
                                                validateField('thresholdPercent', newValue);
                                            }}
                                            aria-label="plus"
                                            disabled={isSensorSilo}
                                        >
                                            +
                                        </button>
                                    </span>
                                </div>
                                {errors.thresholdPercent && <span className={styles.errorText}>{errors.thresholdPercent}</span>}
                            </div>
                        </div>
                    </div>
                </div>

                {errors.general && (
                    <div className={styles.errorMessage}>
                        <span className={styles.errorText}>{errors.general}</span>
                    </div>
                )}

                <div className={styles.actions}>
                    <Button isSecondary onClick={() => window.history.back()}>
                        Zurück
                    </Button>
                    <Button onClick={onSave} disabled={isSaving}>
                        {isSaving ? 'Speichern...' : 'Speichern'}
                    </Button>
                </div>
            </div>
        </LoadingSpinner>
    );
};

export default SiloAnlegen;
